import { useMemo } from "react";
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { RiskLevel, SaasActivity } from "./saasTypes";
import { CATEGORY_COLORS, categoryLabel, formatBucket, formatNumber, humanize, RISK_COLORS, RISK_ORDER } from "./saasFormat";

const MAX_SERIES = 6;

export function ActivityTimelineChart({ activity, height = 260 }: { activity: SaasActivity; height?: number }) {
    const { data, series } = useMemo(() => {
        const totals = new Map<string, number>();
        activity.timeline.forEach((point) =>
            Object.entries(point.categories).forEach(([category, count]) => totals.set(category, (totals.get(category) ?? 0) + count)),
        );
        const ranked = [...totals.entries()].sort((left, right) => right[1] - left[1]).map(([category]) => category);
        const top = ranked.slice(0, MAX_SERIES);
        const hasOther = ranked.length > MAX_SERIES;
        const rows = activity.timeline.map((point) => {
            const row: Record<string, number | string> = { bucket: formatBucket(point.bucket, activity.interval) };
            let other = 0;
            Object.entries(point.categories).forEach(([category, count]) => {
                if (top.includes(category)) row[category] = count;
                else other += count;
            });
            top.forEach((category) => {
                if (row[category] === undefined) row[category] = 0;
            });
            if (hasOther) row.__other = other;
            return row;
        });
        return { data: rows, series: hasOther ? [...top, "__other"] : top };
    }, [activity]);

    if (activity.eventCount === 0) {
        return (
            <div className="flex items-center justify-center text-sm text-muted-foreground" style={{ height }}>
                No activity recorded in this period
            </div>
        );
    }

    return (
        <div>
            <ResponsiveContainer width="100%" height={height}>
                <AreaChart data={data} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                    <defs>
                        {series.map((key) => (
                            <linearGradient key={key} id={`saas-fill-${key}`} x1="0" y1="0" x2="0" y2="1">
                                <stop offset="0%" stopColor={CATEGORY_COLORS[key] ?? "#94a3b8"} stopOpacity={0.45} />
                                <stop offset="100%" stopColor={CATEGORY_COLORS[key] ?? "#94a3b8"} stopOpacity={0.04} />
                            </linearGradient>
                        ))}
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false}
                           minTickGap={24} />
                    <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickLine={false} axisLine={false} allowDecimals={false} />
                    <Tooltip
                        contentStyle={{
                            background: "hsl(var(--card))",
                            border: "1px solid hsl(var(--border))",
                            borderRadius: 10,
                            fontSize: 12,
                            boxShadow: "0 2px 8px rgb(15 23 42 / 0.08)",
                        }}
                        formatter={(value, name) => [formatNumber(Number(value)), name === "__other" ? "Other" : categoryLabel(String(name))]}
                    />
                    {series.map((key) => (
                        <Area
                            key={key}
                            type="monotone"
                            dataKey={key}
                            stackId="activity"
                            stroke={CATEGORY_COLORS[key] ?? "#94a3b8"}
                            fill={`url(#saas-fill-${key})`}
                            strokeWidth={1.5}
                        />
                    ))}
                </AreaChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                {series.map((key) => (
                    <span key={key} className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: CATEGORY_COLORS[key] ?? "#94a3b8" }} />
                        {key === "__other" ? "Other" : categoryLabel(key)}
                    </span>
                ))}
            </div>
        </div>
    );
}

export function SeverityDonut({ counts, total, label }: { counts: Record<string, number>; total: number; label: string }) {
    const data = RISK_ORDER.map((level) => ({ level, value: counts[level] ?? 0 })).filter((entry) => entry.value > 0);
    return (
        <div className="flex items-center gap-5">
            <div className="relative w-36 h-36 flex-shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={data.length ? data : [{ level: "INFO", value: 1 }]}
                            dataKey="value"
                            innerRadius={48}
                            outerRadius={66}
                            paddingAngle={data.length > 1 ? 2 : 0}
                            stroke="none"
                        >
                            {(data.length ? data : [{ level: "INFO" as RiskLevel, value: 1 }]).map((entry) => (
                                <Cell key={entry.level} fill={data.length ? RISK_COLORS[entry.level as RiskLevel] : "hsl(var(--border))"} />
                            ))}
                        </Pie>
                    </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                    <span className="text-2xl font-bold leading-none">{formatNumber(total)}</span>
                    <span className="text-[11px] text-muted-foreground mt-1">{label}</span>
                </div>
            </div>
            <div className="space-y-1.5 flex-1 min-w-0">
                {RISK_ORDER.map((level) => (
                    <div key={level} className="flex items-center justify-between text-xs gap-3">
                        <span className="flex items-center gap-2">
                            <span className="w-2.5 h-2.5 rounded-full" style={{ background: RISK_COLORS[level] }} />
                            {humanize(level)}
                        </span>
                        <span className="font-semibold">{formatNumber(counts[level] ?? 0)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}
